import Vue from 'vue';
import VueResource from 'vue-resource';

Vue.use(VueResource);

// if the config file has a typo, you'll get weird errors here :(
const ENDPOINT = `${process.env.API_ENDPOINT}api/v1/`;

/** --------------------
 *
 *    Helper functions
 *
 * --------------------- */

const getRequest = (url, token) => Vue.http
  .get(`${ENDPOINT}${url}`, { headers: { Authorization: `Bearer ${token}` } })
  .then(res => res.json());

const postRequest = (url, token, body) => Vue.http.post(`${ENDPOINT}${url}`, body, {
  headers: { Authorization: `Bearer ${token}` }
});

const putRequest = (url, token, body) => Vue.http.put(`${ENDPOINT}${url}`, body, {
  headers: { Authorization: `Bearer ${token}` }
});

const deleteRequest = (url, token) => Vue.http.delete(`${ENDPOINT}${url}`, {
  headers: { Authorization: `Bearer ${token}` }
});

const postAndGetFile = (url, body, token) => Vue.http.post(`${ENDPOINT}${url}`, body, {
  responseType: 'arraybuffer',
  headers: { Authorization: `Bearer ${token}` }
});

// TODO - add pagination to (most/some) of these

/** --------------------
 *
 *    Customer endpoints
 *
 * --------------------- */

const getCustomers = token => getRequest('customers', token);

const addCustomer = (newCustomer, token) => postRequest('customers', token, newCustomer);

/** --------------------
 *
 *    Programme endpoints
 *
 * --------------------- */

const getProgrammes = token => getRequest('programmes', token);

const addProgramme = (programmeName, token) => {
  // eslint-disable-line camelcase
  const body = {
    name: programmeName
  };

  return postRequest('programmes', token, body);
};

/** --------------------
 *
 *    Survey endpoints
 *
 * --------------------- */

/**
 * @TODO - MOVE reporting and analysis to REporting.js
 */

const getSurvey = (id, token) => getRequest(`surveys/${id}`, token);

const getSurveys = token => getRequest('surveys', token);

const getSurveyRuns = token => getRequest('survey-runs', token);

const searchSurveyRuns = (searchBody, token) => postRequest('survey-runs/search', token, searchBody);

const getSurveyResponsePage = (id, page, token) => getRequest(`surveys/${id}/responses/page/${page}`, token);

const getExcelReport = (surveyRunId, dimensionData, type, token) => postAndGetFile(`survey-runs/${surveyRunId}/xlsx?type=${type}`, dimensionData, token);

const getMultiReport = (surveyRunIds, token) => postAndGetFile('survey-runs/multi/xlsx', surveyRunIds, token);

const getScores = (surveyRunIds, dimensionData, token) => postRequest(`survey-runs/${surveyRunIds.length > 1 ? 'multiple' : surveyRunIds[0]}/analyze?${surveyRunIds.length > 1 ? `survey_run_id=${JSON.stringify(surveyRunIds)}` : ''}`, token, dimensionData);

const getIndustryAverages = (programmeId, token) => getRequest(`programmes/${programmeId}/industry-averages`, token);

const getSurveyRunSummary = (surveyRunId, token) => getRequest(`survey-runs/${surveyRunId}/summary`, token);

const deleteSurveyRun = (surveyRunId, token) => deleteRequest(`survey-runs/${surveyRunId}`, token);

const getRecommendations = (surveyRunId, token) => getRequest(`survey-runs/${surveyRunId}/recommendations`, token);

const saveRecommendation = (recommendationId, surveyRunId, recommendation, token) => recommendationId ? postRequest(`survey-runs/${surveyRunId}/recommendations/${recommendationId}`, token, {recommendation}) : postRequest(`survey-runs/${surveyRunId}/recommendations`, token, recommendation);

const getQuickWins = (surveyRunId, token) => getRequest(`survey-runs/${surveyRunId}/quick-wins`, token);

const saveQuickWin = (quickWinId, surveyRunId, quickWin, token) => quickWinId ? postRequest(`survey-runs/${surveyRunId}/quick-wins/${quickWinId}`, token, {quick_win: quickWin}) : postRequest(`survey-runs/${surveyRunId}/quick-wins`, token, quickWin);

const deleteQuickWin = (quickWinId, surveyRunId, token) => deleteRequest(`survey-runs/${surveyRunId}/quick-wins/${quickWinId}`, token);

const deleteRecommendation = (recommendationId, surveyRunId, token) => deleteRequest(`survey-runs/${surveyRunId}/recommendations/${recommendationId}`, token);

const deleteGoalSetting = (surveyRunId, goalSettingId, token) => deleteRequest(`survey-runs/${surveyRunId}/goal-setting/${goalSettingId}`, token);

const deleteGPCSession = (customerId, sessionId, token) => deleteRequest(`customers/${customerId}/gpc-sessions/${sessionId}`, token);

const saveGPCSession = (customerId, gpcSessionId, session, token) => gpcSessionId ? postRequest(`customers/${customerId}/gpc-sessions/${gpcSessionId}`, token, session) : postRequest(`customers/${customerId}/gpc-sessions`, token, session);

const searchGPCSessions = (searchBody, token) => postRequest(`customers/${searchBody.customer_id ? searchBody.customer_id : 'all'}/gpc-sessions/search`, token, searchBody);

const searchQuickWinsAndRecommendations = (type, searchBody, token) => postRequest(`survey-runs/all/${type}/search`, token, searchBody);

const searchGoalSettings = (searchBody, token) => postRequest(`survey-runs/all/goal-setting/search`, token, searchBody);

const saveGoalSetting = (surveyRunId, goalSettingId, body, token) => goalSettingId ? putRequest(`survey-runs/${surveyRunId}/goal-setting/${goalSettingId}`, token, body): postRequest(`survey-runs/${surveyRunId}/goal-setting`, token, body);

const addSurveyRun = (body, token) => postRequest(`survey-runs`, token, body);

const updateSurveyRun = (surveyRunId, body, token) => putRequest(`survey-runs/update/${surveyRunId}`, token, body);                                                                                                                                                                                                                                                                                                                                                                                                                                                                                           

const addResponsesToSurveyRun = async (surveyRunId, surveyMonkeyId, questions, token) => {

  const surveyRunResponse = await putRequest(`survey-runs/${surveyRunId}`, token, {questions});

  const firstPage = await getSurveyResponsePage(surveyMonkeyId, 1, token);
  await postRequest(`survey-runs/${surveyRunId}/responses`, token, firstPage);
  if (firstPage.total > firstPage.per_page) {
    for (let i = 2; i <= Math.ceil(firstPage.total / firstPage.per_page); i++) {
      const page = await getSurveyResponsePage(surveyMonkeyId, i, token); // eslint-disable-line no-await-in-loop
      await postRequest(`survey-runs/${surveyRunId}/responses`, token, page); // eslint-disable-line no-await-in-loop
    }
  }
};

export {
  getCustomers,
  addCustomer,
  getProgrammes,
  addProgramme,
  getSurvey,
  getSurveys,
  getSurveyRuns,
  searchSurveyRuns,
  getExcelReport,
  addSurveyRun,
  getSurveyRunSummary,
  getScores,
  getMultiReport,
  saveRecommendation,
  getRecommendations,
  saveQuickWin,
  getQuickWins,
  searchQuickWinsAndRecommendations,
  saveGPCSession,
  searchGPCSessions,
  deleteSurveyRun,
  getIndustryAverages,
  deleteQuickWin,
  deleteRecommendation,
  deleteGPCSession,
  addResponsesToSurveyRun,
  searchGoalSettings,
  deleteGoalSetting,
  saveGoalSetting,
  updateSurveyRun
};



// WEBPACK FOOTER //
// ./src/services/API.js